import React, { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, Wallet, Sparkles, Award, Phone, User, Mail, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const COURSES = [
  "Mechatronics (3.5 yrs, Earn While Learn)",
  "Tool & Die Making (3.5 yrs)",
  "Precision Machinist (3.5 yrs)",
  "CNC Programming (6 months)",
  "Industrial Automation - PLC (4 months)",
  "Welding Technology (3 months)",
  "Not sure — please advise",
];

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  course_interest: COURSES[0],
  qualification: "12th Pass",
  city: "",
  message: "",
};

export default function ApplyModal({ open, onClose, defaultCourse }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const courseOptions = defaultCourse && !COURSES.includes(defaultCourse) ? [defaultCourse, ...COURSES] : COURSES;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (open && defaultCourse) {
      setForm((f) => ({ ...f, course_interest: defaultCourse }));
    }
  }, [open, defaultCourse]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => {
    onClose?.();
    setTimeout(() => { setDone(false); setForm(EMPTY); }, 400);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admissions", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        qualification: form.qualification,
        course_interest: form.course_interest,
        city: form.city.trim(),
        message: form.message.trim() || "Application via Apply Now button",
      });
      setDone(true);
      toast({ title: "Application received!", description: "Our counsellor will call you shortly." });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 animate-fade-up"
      role="dialog"
      aria-modal="true"
      aria-labelledby="apply-title"
      data-testid="apply-modal"
    >
      <div className="absolute inset-0 bg-[#04102e]/70 backdrop-blur-sm" onClick={close} />

      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden grid md:grid-cols-5 max-h-[92vh] overflow-y-auto">
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 hover:bg-slate-100 text-slate-700 shadow flex items-center justify-center transition"
          aria-label="Close"
          data-testid="apply-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Left panel */}
        <div className="relative md:col-span-2 bg-[#0B2A6B] text-white p-8 flex flex-col justify-between overflow-hidden">
          <div className="absolute -top-16 -left-16 w-56 h-56 bg-yellow-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -right-8 w-56 h-56 bg-blue-400/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[10px] font-black uppercase tracking-widest">
              <Sparkles className="h-3 w-3" /> Admissions Open • 2026
            </div>
            <h2 id="apply-title" className="text-3xl sm:text-4xl font-black leading-tight mt-4">
              Apply for <span className="text-yellow-400">Earn While You Learn</span>
            </h2>
            <p className="text-sm text-slate-200 mt-3 leading-relaxed">
              3½ years German Technical Training Course. HWK Koblenz + Govt of India dual certified. Get placed at Maruti, Motherson, Stryker & 60+ manufacturers.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm">
              <li className="flex items-center gap-2"><Wallet className="h-4 w-4 text-yellow-400 shrink-0" />Earn a monthly stipend from Year 2</li>
              <li className="flex items-center gap-2"><Award className="h-4 w-4 text-yellow-400 shrink-0" />Dual certificate — Germany + India</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-yellow-400 shrink-0" />95%+ placement track record</li>
            </ul>
          </div>
          <div className="relative mt-8 pt-6 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-black">Prefer to talk?</div>
            <a href="tel:+918800288994" className="mt-2 inline-flex items-center gap-2 text-lg font-bold text-white hover:text-yellow-400 transition">
              <Phone className="h-4 w-4" /> +91 88002 88994
            </a>
          </div>
        </div>

        {/* Right panel */}
        <div className="md:col-span-3 p-8">
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-8">
              <div className="h-16 w-16 rounded-full bg-yellow-400 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-9 w-9 text-[#0B2A6B]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B2A6B]">Application received!</h3>
              <p className="text-slate-600 mt-2 max-w-sm">
                Thanks {form.full_name.split(" ")[0] || "there"} — our counsellor will call you on <span className="font-semibold text-[#0B2A6B]">{form.phone}</span> shortly.
              </p>
              <button type="button" onClick={close} className="mt-6 px-6 py-2.5 rounded-lg btn-primary" data-testid="apply-modal-close-success">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3.5" data-testid="apply-modal-form">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-yellow-600">Quick application</p>
                <h3 className="text-2xl font-bold text-[#0B2A6B] mt-1">Start your application</h3>
                <p className="text-sm text-slate-500 mt-1">Share your details — we'll call within one working day.</p>
              </div>

              <IconField icon={User}>
                <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Your full name *" className="w-full bg-transparent outline-none text-slate-800 text-sm" data-testid="apply-input-name" />
              </IconField>

              <div className="grid sm:grid-cols-2 gap-3">
                <IconField icon={Phone}>
                  <input required type="tel" pattern="[0-9+ ]{7,15}" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone *" className="w-full bg-transparent outline-none text-slate-800 text-sm" data-testid="apply-input-phone" />
                </IconField>
                <IconField icon={Mail}>
                  <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email *" className="w-full bg-transparent outline-none text-slate-800 text-sm" data-testid="apply-input-email" />
                </IconField>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <SelectField label="Qualification" value={form.qualification} onChange={(v) => update("qualification", v)} options={["10th Pass", "12th Pass", "ITI", "Diploma", "Degree", "Other"]} testid="apply-input-qualification" />
                <SelectField label="City" value={form.city} onChange={(v) => update("city", v)} options={["Gurugram", "Delhi", "Noida", "Faridabad", "Manesar", "Other"]} testid="apply-input-city" raw />
              </div>

              <SelectField label="Course interest" value={form.course_interest} onChange={(v) => update("course_interest", v)} options={courseOptions} testid="apply-input-course" />

              <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg btn-primary text-base disabled:opacity-70" data-testid="apply-submit-btn">
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" />Sending...</>) : (<>Submit Application</>)}
              </button>

              <p className="text-[11px] text-slate-400 text-center">
                By submitting you agree to be contacted about admissions. We never share your data.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function IconField({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 h-11 px-3 rounded-lg border border-slate-200 bg-slate-50 focus-within:border-yellow-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-yellow-100 transition">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      {children}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, raw }) {
  return (
    <label className="block relative">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">{label}</span>
      {raw ? (
        <div className="relative">
          <input list={`${testid}-list`} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Type or pick" className="w-full h-11 px-3 pr-8 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-100 text-sm text-slate-800 transition" data-testid={testid} />
          <datalist id={`${testid}-list`}>{options.map((o) => (<option key={o} value={o} />))}</datalist>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        </div>
      ) : (
        <div className="relative">
          <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 px-3 pr-8 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-100 text-sm text-slate-800 appearance-none transition" data-testid={testid}>
            {options.map((o) => (<option key={o} value={o}>{o}</option>))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        </div>
      )}
    </label>
  );
}
